import { getAVUtil } from '../../../utils/getAVUtil';
import { findNegotiationCaseByParcelId, getConversionFactor } from '../../../utils/conversionUtils';
import { uploadFileToS3Auto } from '../Offers/s3UploadService';
import { usePersistedDataStore } from '../../store';

const HEADERS = [
  'Parcel ID',
  'Address',
  'Owner Name',
  'SCAR Index',
  'Assessment',
  'New Settlement (AV)',
  'New Settlement ($)',
];

const ownerName = (row, caseObject) => {
  const first = caseObject?.OwnerFirstName || row?.OwnerFirstName || '';
  const last = caseObject?.OwnerLastName || row?.OwnerLastName || '';
  const combined = `${first} ${last}`.trim();
  return combined || caseObject?.ClientName || row?.ClientName || '';
};

const settlementDollars = (settlementAv, eq, rar) => {
  if (!Number.isFinite(settlementAv) || settlementAv <= 0) return '';
  try {
    const factor = getConversionFactor(eq, rar);
    return factor > 0 ? Math.round(settlementAv / factor / 10) * 10 : Math.round(settlementAv);
  } catch {
    return '';
  }
};

const settlementFilename = (negotiationObj) => {
  const muniString = negotiationObj?.muniString || 'Unknown';
  const currentDate = new Date();
  const month = String(currentDate.getMonth() + 1).padStart(2, '0');
  const day = String(currentDate.getDate()).padStart(2, '0');
  const year = String(currentDate.getFullYear()).slice(-2);
  return `Aventine_${muniString}_Settlements_${month}_${day}_${year}.xlsx`;
};

/**
 * Stripped settlement workbook: parcel, address, owner, SCAR index, starting assessment, settlement MV and AV.
 * @param {Array} settledRows
 * @returns {Promise<{ downloadUrl: string, csvFilename: string }>}
 */
export const uploadSettlementXls = async (settledRows) => {
  const negotiationObj = usePersistedDataStore.getState().negotiationObj;
  const ExcelJS = (await import('exceljs')).default;
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Settlements');
  sheet.addRow(HEADERS);

  settledRows.forEach((row) => {
    const caseObject = findNegotiationCaseByParcelId(negotiationObj, row.parcel_id) || row;
    const assessment = getAVUtil(caseObject) ?? getAVUtil(row);
    const settlementAv = Number(row.SettlementValue);
    sheet.addRow([
      row.parcel_id || '',
      row.Address || caseObject.Address || '',
      ownerName(row, caseObject),
      row.SCARIndexNumber || caseObject.SCARIndexNumber || '',
      assessment == null || assessment === '' ? '' : Math.round(Number(assessment)),
      Number.isFinite(settlementAv) ? Math.round(settlementAv) : '',
      settlementDollars(settlementAv, caseObject.EQ ?? row.EQ, caseObject.RAR ?? row.RAR),
    ]);
  });

  sheet.getRow(1).font = { bold: true };
  [5, 6, 7].forEach((col) => { sheet.getColumn(col).numFmt = '#,##0'; });
  [16, 28, 24, 16, 14, 22, 20].forEach((width, i) => { sheet.getColumn(i + 1).width = width; });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const csvFilename = settlementFilename(negotiationObj);
  const downloadUrl = await uploadFileToS3Auto(
    blob,
    `Settlements/settlements_${Date.now()}.xlsx`,
    'negotiation-files',
    csvFilename
  );
  return { downloadUrl, csvFilename };
};
